import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Component Imports
import { AGTable } from "@/components";
import { AlgoConfigDetail } from "../AlgoConfigDetail";

// Hooks Imports
import { useAGTable } from "./hooks/useAGTable";
import { useFetchStrategyInstancesQuery } from "../../services/algoManagementApi";
import { useAlerts } from "@/hooks";

// Types Imports
import { IAGAlgoDataRow, NEW_STRATEGY_ROW_ID, useAGTableProps } from "./AlgoTable.types";

// Utils Imports
import { getErrorMessage } from "../../../../utils/getErrorMessage";
import { mapRowData } from "./utils/mapRowData";

const AlgoTable = ({
  interactionsData,
}: Pick<useAGTableProps, "interactionsData">) => {
  // States - START --------------------------------------------------------------
  const { showError } = useAlerts();

  const [dataMap, setDataMap] = useState<Map<string, IAGAlgoDataRow>>(
    () => new Map(),
  );

  // Store previous data for comparison
  const previousDataRef = useRef<Map<string, IAGAlgoDataRow>>(new Map());
  // States - END ----------------------------------------------------------------

  // Hooks - START --------------------------------------------------------------
  const {
    gridRef,
    rowData,
    colDefs,
    defaultColDef,
    sideBar,
  } = useAGTable({ dataMap });
  const {
    data: strategies,
    isLoading: isLoadingStrategies,
    isError: isErrorStrategies,
    error: errorStrategies,
  } = useFetchStrategyInstancesQuery(undefined, {
    pollingInterval: 1000,
    skipPollingIfUnfocused: true,
  });
  // Hooks - END ----------------------------------------------------------------

  // useEffect - START ----------------------------------------------------------
  // Transform and update data incrementally
  useEffect(() => {
    if (!strategies) return;

    const newRows = mapRowData(strategies);
    const newDataMap = new Map<string, IAGAlgoDataRow>();

    // Build new map with unique keys
    newRows.forEach((row) => {
      const key = `${row.connection_id}-${row.strategy_instance_id}`;
      newDataMap.set(key, row);
    });

    setDataMap(newDataMap);

    // Use AG Grid's transaction API for efficient updates
    if (gridRef.current?.api) {
      const api = gridRef.current.api;
      const previousData = previousDataRef.current;

      const toAdd: IAGAlgoDataRow[] = [];
      const toUpdate: IAGAlgoDataRow[] = [];
      const toRemove: IAGAlgoDataRow[] = [];

      // Find new and updated rows
      newDataMap.forEach((newRow, key) => {
        const oldRow = previousData.get(key);
        if (!oldRow) {
          toAdd.push(newRow);
        } else if (JSON.stringify(oldRow) !== JSON.stringify(newRow)) {
          toUpdate.push(newRow);
        }
      });

      // Find removed rows
      previousData.forEach((_, key) => {
        if (!newDataMap.has(key)) {
          toRemove.push({ id: key } as IAGAlgoDataRow);
        }
      });

      // Apply transaction (AG Grid will only update changed rows)
      if (toAdd.length > 0 || toUpdate.length > 0 || toRemove.length > 0) {
        api.applyTransaction({
          add: toAdd,
          update: toUpdate,
          remove: toRemove,
        });

        // Flash updated rows
        if (toUpdate.length > 0) {
          const updatedRowNodes = toUpdate
            .map((row) => api.getRowNode(row.id))
            .filter((node) => node !== undefined);

          if (updatedRowNodes.length > 0) {
            api.flashCells({
              rowNodes: updatedRowNodes,
            });
          }
        }
      }
    }

    previousDataRef.current = newDataMap;
  }, [strategies, gridRef]);

  useEffect(() => {
    if (isErrorStrategies) {
      showError(
        `Failed to fetch Strategy Instances: ${getErrorMessage(errorStrategies)}`,
      );
    }
  }, [isErrorStrategies, errorStrategies, showError]);
  // useEffect - END ------------------------------------------------------------

  const detailContext = useMemo(
    () => ({ interactionsData }),
    [interactionsData]
  );

  const TABLE_HEIGHT = 750;
  const HEADER_HEIGHT = 48;
  const MASTER_ROW_HEIGHT = 36;
  const DETAIL_ROW_HEIGHT = Math.round((TABLE_HEIGHT - HEADER_HEIGHT - MASTER_ROW_HEIGHT) * 0.95);

  const getRowHeight = useCallback((params: any) => {
    if (params.node?.detail) return DETAIL_ROW_HEIGHT;
    return undefined;
  }, [DETAIL_ROW_HEIGHT]);

  const handleRowGroupOpened = useCallback((event: any) => {
    if (!event?.expanded || !event?.node || !event?.api) return;
    event.api.forEachNode((n: any) => {
      if (n.master && n !== event.node && n.expanded) {
        n.setExpanded(false);
      }
    });
    requestAnimationFrame(() => {
      try {
        event.api.ensureNodeVisible(event.node, "top");
      } catch {
        /* node may have re-collapsed before scroll */
      }
    });
  }, []);

  const getRowStyle = useCallback((params: any) => {
    if (params.data?.id === NEW_STRATEGY_ROW_ID) {
      return {
        backgroundColor: "rgba(28, 46, 87, 0.25)",
        borderTop: "1px dashed #2a3f6f",
        borderBottom: "1px dashed #2a3f6f",
      } as any;
    }
    return undefined;
  }, []);

  return (
    <AGTable
      gridRef={gridRef}
      rowData={rowData}
      colDefs={colDefs}
      defaultColDef={defaultColDef}
      groupDisplayType="singleColumn"
      sideBar={sideBar}
      animateRows={false}
      loading={isLoadingStrategies}
      tableHeight={750}
      suppressCellFocus
      suppressColumnVirtualisation
      suppressRowVirtualisation
      suppressRowHoverHighlight
      debounceVerticalScrollbar
      context={detailContext}
      masterDetail
      detailCellRenderer={AlgoConfigDetail}
      getRowHeight={getRowHeight}
      getRowStyle={getRowStyle}
      onRowGroupOpened={handleRowGroupOpened}
    />
  );
};

export default AlgoTable;
